/**
 * api.js
 * Thin fetch wrapper for all backend report endpoints.
 * Uses the CRA proxy (http://localhost:3001) in dev,
 * and REACT_APP_API_URL in production.
 */

const BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Core fetch helper — throws on non-OK responses with the error body.
 */
async function apiFetch(path, options = {}) {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

/** Build query string from a params object, omitting null/undefined values. */
function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

// --------------------------------------------------------------------------
// Report endpoints
// --------------------------------------------------------------------------

export const api = {
  /** Top-level KPI summary. */
  getSummary: (params) =>
    apiFetch(`/api/reports/summary${buildQuery(params)}`),

  /** Sales velocity table. */
  getSales: (params) =>
    apiFetch(`/api/reports/sales${buildQuery(params)}`),

  /** Full inventory report with runway + flags. */
  getInventory: (params) =>
    apiFetch(`/api/reports/inventory${buildQuery(params)}`),

  /** Clearance / dead stock list. */
  getDeadStock: (params) =>
    apiFetch(`/api/reports/dead-stock${buildQuery(params)}`),

  /** Geo performance report. */
  getGeo: (params) =>
    apiFetch(`/api/reports/geo${buildQuery(params)}`),

  /** Product type aggregation. */
  getByType: (params) =>
    apiFetch(`/api/reports/by-type${buildQuery(params)}`),

  /** Vendor aggregation. */
  getByVendor: (params) =>
    apiFetch(`/api/reports/by-vendor${buildQuery(params)}`),

  /** Period-over-period comparison data. */
  getComparison: (params) =>
    apiFetch(`/api/reports/comparison${buildQuery(params)}`),

  /** Low stock reorder alerts. */
  getReorderAlerts: (params) =>
    apiFetch(`/api/reports/reorder-alerts${buildQuery(params)}`),

  /** Available filter options. */
  getFilters: (params) =>
    apiFetch(`/api/reports/filters${buildQuery(params)}`),

  /** Clear server-side cache. */
  clearCache: () =>
    apiFetch('/api/reports/cache/clear', { method: 'POST' }),
};
